import type { CarrierRateRow } from './freight-rate-card-types';
import type { LtlQuote } from '../../types/ltl';

/**
 * Grouping key for a carrier. carrierScac is preferred, but an empty SCAC
 * should fall through to carrierName — so we intentionally use `||` (not `??`)
 * here so the empty string is treated as falsy.
 */
const getCarrierKey = (quote: LtlQuote): string => quote.carrierScac || quote.carrierName;

export const hasInsurance = (quote: LtlQuote): boolean => Boolean(quote.insurancePremium && quote.insurancePremium > 0);

/**
 * Picks the cheaper of two quotes by totalCharges. When charges tie, keeps the
 * incumbent (`current`) for deterministic, stable ordering.
 */
const pickCheaper = (current: LtlQuote | null, candidate: LtlQuote): LtlQuote => {
  if (current === null) {
    return candidate;
  }
  return candidate.totalCharges < current.totalCharges ? candidate : current;
};

/**
 * Groups a filtered list of quotes into one row per carrier, splitting each
 * carrier's offers into a standard and a guaranteed slot. When a carrier has
 * multiple offers on a side, the cheapest (by totalCharges) is kept. Carrier
 * order follows first-seen order for determinism.
 */
const buildCarrierRows = (quotes: LtlQuote[]): CarrierRateRow[] => {
  const rowsByKey = new Map<string, CarrierRateRow>();

  return quotes.reduce<CarrierRateRow[]>((rows, quote) => {
    const carrierKey = getCarrierKey(quote);
    const existing = rowsByKey.get(carrierKey);

    if (existing) {
      if (quote.isGuaranteed) {
        existing.guaranteed = pickCheaper(existing.guaranteed, quote);
      } else {
        existing.standard = pickCheaper(existing.standard, quote);
      }
      return rows;
    }

    const row: CarrierRateRow = {
      carrierKey,
      carrierName: quote.carrierName,
      carrierScac: quote.carrierScac,
      standard: quote.isGuaranteed ? null : quote,
      guaranteed: quote.isGuaranteed ? quote : null,
    };
    rowsByKey.set(carrierKey, row);
    rows.push(row);
    return rows;
  }, []);
};

export type GroupedRates = {
  withoutInsurance: CarrierRateRow[];
  withInsurance: CarrierRateRow[];
};

/**
 * Splits quotes into two lists by insurance, then groups each by carrier.
 * - `withoutInsurance`: offers where `insurancePremium` is null/0.
 * - `withInsurance`: offers where `insurancePremium` > 0.
 *
 * Within each list, quotes are grouped by carrier with standard/guaranteed
 * slots (cheapest kept when duplicates exist).
 */
export const groupRates = (quotes: LtlQuote[]): GroupedRates => {
  const uninsured: LtlQuote[] = [];
  const insured: LtlQuote[] = [];

  for (const quote of quotes) {
    if (hasInsurance(quote)) {
      insured.push(quote);
    } else {
      uninsured.push(quote);
    }
  }

  return {
    withoutInsurance: buildCarrierRows(uninsured),
    withInsurance: buildCarrierRows(insured),
  };
};
